import React from 'react';
import { Box, Text } from '@react-three/drei';
import { Vector3 } from 'three';
import { useGameStore, TowerType } from '../store/gameStore';
import { useResponsive } from '../hooks/useResponsive';

interface TowerPreviewProps {
  position: Vector3;
  type: TowerType;
}

const TowerPreview: React.FC<TowerPreviewProps> = ({ position, type }) => {
  const { isMobile, isTablet, scaleFactor } = useResponsive();

  // Get tower color based on type
  const getTowerColor = (type: TowerType): string => {
    switch (type) {
      case 'science':
        return '#4a4ae0';
      case 'math':
        return '#e04a4a';
      case 'art':
        return '#4ae04a';
      case 'drama':
        return '#9932cc';
      default:
        return '#4a4ae0';
    }
  };

  const color = getTowerColor(type);

  // Adjust size based on device
  const baseSize = isMobile ? 0.8 : 1;
  const height = isMobile ? 1.6 : 2;
  const textScale = isMobile ? 0.24 : 0.3;

  return (
    <group position={[position.x, 0, position.z]}>
      {/* Base */}
      <Box
        args={[baseSize, 0.2, baseSize]}
        position={[0, 0.1, 0]}
        castShadow
        receiveShadow
      >
        <meshStandardMaterial
          color={color}
          transparent
          opacity={0.3}
          side={2}
        />
      </Box>

      {/* Tower body */}
      <Box
        args={[baseSize * 0.8, height, baseSize * 0.8]}
        position={[0, height / 2, 0]}
        castShadow
        receiveShadow
      >
        <meshStandardMaterial
          color={color}
          transparent
          opacity={0.5}
          side={2}
        />
      </Box>

      {/* Tower top */}
      <Box
        args={[baseSize * 0.6, 0.4, baseSize * 0.6]}
        position={[0, height + 0.2, 0]}
        castShadow
        receiveShadow
      >
        <meshStandardMaterial
          color={color}
          transparent
          opacity={0.7}
          side={2}
        />
      </Box>

      {/* Placement indicator ring */}
      <mesh position={[0, 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[baseSize * 1.2, baseSize * 1.4, 32]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.5}
        />
      </mesh>

      {/* Tower type label */}
      <Text
        position={[0, height + 0.8, 0]}
        fontSize={textScale}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.01}
        outlineColor="#000000"
        maxWidth={baseSize * 2}
        scale={[scaleFactor, scaleFactor, scaleFactor]}
      >
        {type.toUpperCase()}
      </Text>
    </group>
  );
};

export default TowerPreview; 